import { Trans } from '@lingui/macro'
import { Loading } from 'components'
import { css, styled, styles } from 'helpers'
import React, { Component } from 'react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import {
  CellMeasurer,
  CellMeasurerCache
} from 'react-virtualized/dist/commonjs/CellMeasurer'
import MultiGridComponent from 'react-virtualized/dist/commonjs/MultiGrid'
import { Typography } from 'webbook-airways'

export default class MultiGrid extends Component {
  multigrid = undefined

  state = {
    activeRow: undefined
  }

  _cache = new CellMeasurerCache({
    defaultHeight: 30,
    defaultWidth: 150,
    fixedHeight: true
  })

  render() {
    const {
      title,
      centerColumns,
      headerList,
      bodyList,
      loading,
      blockLoading,
      onClick
    } = this.props

    const columnWidth = 200
    const rowHeight = 30
    const rowCount = bodyList.length + 1
    const columnCount = headerList ? headerList.length : bodyList.length

    if (rowCount === 1) {
      if (loading || blockLoading) {
        return (
          <Loading size={48}>
            <Trans>Loading</Trans>
          </Loading>
        )
      }
      return (
        <Typography variant="caption" color="primary">
          No data available
        </Typography>
      )
    } else if (blockLoading) {
      return (
        <Loading size={48}>
          <Trans>Loading</Trans>
        </Loading>
      )
    }
    return (
      <React.Fragment>
        {title && (
          <div style={{ marginLeft: 5 }}>
            <Typography
              style={{
                color: styles.colors.primary,
                margin: 5,
                userSelect: 'none',
                fontWeight: 500
              }}
            >
              {title}
            </Typography>
          </div>
        )}
        <AutoSizer>
          {({ height, width }) => {
            let heightMultiplier = 0.1
            let minWidth = columnWidth * columnCount
            const minHeight = rowHeight * rowCount

            if (width < minWidth) {
              heightMultiplier = 0.5
            }

            const maxHeight = Math.min(
              height,
              minHeight + rowHeight * heightMultiplier
            )
            const maxWidth = Math.min(width, minWidth, 1156)
            return (
              <React.Fragment>
                <MultiGridComponent
                  ref={multigrid => (this.multigrid = multigrid)}
                  enableFixedColumnScroll
                  enableFixedRowScroll
                  hideTopRightGridScrollbar
                  hideBottomLeftGridScrollbar
                  // fixedColumnCount={fixedColumnCount}
                  fixedColumnCount={0}
                  fixedRowCount={1}
                  rowHeight={this._cache.rowHeight}
                  rowCount={rowCount}
                  deferredMeasurementCache={this._cache}
                  columnWidth={this._cache.columnWidth}
                  columnCount={columnCount}
                  height={maxHeight}
                  width={maxWidth}
                  cellRenderer={({
                    key,
                    parent,
                    columnIndex,
                    rowIndex,
                    style
                  }) => {
                    const isHeader =
                      headerList && headerList.length > 0 && rowIndex === 0
                    const isBody = bodyList[rowIndex - 1]
                    let value
                    let center = false

                    if (isBody) {
                      value = bodyList[rowIndex - 1][columnIndex]
                    }
                    if (Array.isArray(centerColumns)) {
                      center = centerColumns.includes(columnIndex)
                    }

                    return (
                      <CellMeasurer
                        key={key}
                        cache={this._cache}
                        columnIndex={columnIndex}
                        parent={parent}
                        rowIndex={rowIndex}
                      >
                        {isHeader ? (
                          <HeaderCell
                            style={{
                              justifyContent: center ? 'center' : 'flex-start',
                              ...style
                            }}
                          >
                            <HeaderText>{headerList[columnIndex]}</HeaderText>
                          </HeaderCell>
                        ) : isBody ? (
                          <Cell
                            style={{
                              justifyContent: center ? 'center' : 'flex-start',
                              ...style
                            }}
                            clickable={onClick}
                            onMouseEnter={() =>
                              this.setState({ activeRow: rowIndex })
                            }
                            onMouseLeave={() =>
                              this.setState(state => ({
                                activeRow:
                                  state.activeRow === rowIndex && undefined
                              }))
                            }
                            active={this.state.activeRow === rowIndex}
                            onClick={
                              onClick
                                ? event => onClick(rowIndex - 1, event)
                                : undefined
                            }
                          >
                            <Text>
                              {typeof value === 'number'
                                ? value.toLocaleString('da-DK')
                                : value}
                            </Text>
                          </Cell>
                        ) : null}
                      </CellMeasurer>
                    )
                  }}
                  style={{
                    borderRadius: 4,
                    boxShadow: '0px 2px 1px rgba(0,0,0,0.1)',
                    backgroundColor: '#fff',
                    overflow: 'hidden'
                  }}
                  styleTopLeftGrid={{
                    borderBottom: '1px solid #e9ecf0',
                    borderRight: '1px solid #e9ecf0'
                  }}
                  styleTopRightGrid={{
                    borderBottom: '1px solid #e9ecf0'
                  }}
                  styleBottomLeftGrid={{
                    borderRight: '1px solid #e9ecf0'
                  }}
                  styleBottomRightGrid={{ outline: 0 }}
                />
                {loading && (
                  <div
                    className={css({
                      position: 'absolute',
                      left: 10,
                      right: 10,
                      top: maxHeight + 10,
                      display: 'flex',
                      alignItems: 'center'
                    })}
                  >
                    <Loading
                      container={false}
                      size={24}
                      style={{ marginRight: 15 }}
                    />
                    <Typography variant="caption" style={{ fontWeight: 700 }}>
                      <Trans>Loading more</Trans>
                    </Typography>
                  </div>
                )}
              </React.Fragment>
            )
          }}
        </AutoSizer>
      </React.Fragment>
    )
  }
}

const HeaderCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  borderRight: '1px solid #e9ecf0'
})

const HeaderText = styled('span')({
  fontFamily: styles.fonts.body,
  fontSize: 11,
  color: styles.colors.secondary,
  whiteSpace: 'nowrap',
  marginLeft: 12,
  marginRight: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
})

const Cell = styled('div')(({ active, onClick }) => ({
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid #e9ecf0',
  borderRight: '1px solid #e9ecf0',
  backgroundColor: active && 'rgba(0,0,0,0.05)',
  cursor: onClick && 'pointer'
}))

const Text = styled('span')({
  fontFamily: styles.fonts.body,
  fontSize: 11,
  color: styles.colors.primary,
  whiteSpace: 'nowrap',
  marginLeft: 12,
  marginRight: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
})
